import { Plug } from 'lucide-react';

import { Button } from '@/_shared/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/_shared/components/ui/empty';
import { useRudderstackReauth } from '@/_shared/hooks/useRudderstackReauth';

/**
 * Remediation empty-state shown when a surface needs a RudderStack connection that
 * this workspace doesn't have. The connection is established by signing in with
 * RudderStack and linking a workspace (see `/auth/rudderstack`), not in settings.
 *
 * Used on the Sources and Warehouses settings tabs and the chat welcome screen so
 * the "connect RudderStack" prompt looks identical everywhere. `secondaryAction`
 * lets a surface add an extra button (e.g. chat's "Open chats").
 */
export function RudderstackNotConfiguredCard({
  description,
  secondaryAction,
}: {
  description: string;
  secondaryAction?: React.ReactNode;
}): React.ReactElement {
  const reauth = useRudderstackReauth();

  return (
    <Empty className="border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Plug />
        </EmptyMedia>
        <EmptyTitle>Connect RudderStack to get started</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {/* While the probe loads, a disabled button stands in for either path. */}
          {reauth.native || reauth.isLoading ? (
            <Button loading={reauth.pending || reauth.isLoading} onClick={reauth.start}>
              Continue with RudderStack
            </Button>
          ) : (
            <Button asChild>
              <a href={reauth.oauthUrl}>Continue with RudderStack</a>
            </Button>
          )}
          {secondaryAction}
        </div>
      </EmptyContent>
    </Empty>
  );
}
